import React, { useEffect, useState } from 'react';
import ticketService from '../../services/ticketService';
import RecordReferenceSelect from './RecordReferenceSelect';
import MultiReferenceSelect from '../MultiReferenceSelect';
import PaymentGatewayRateTable from '../PaymentGatewayRateTable';
import { buildGatewayRateRules } from '../../utils/paymentGatewayPayload';
import { getTicketProviderDefinition } from '../../utils/ticketRequests';
import { recordCreationFieldGroups, getRecordCreationFieldGroup } from '../../utils/recordCreationTickets';

const fieldGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: '1rem' };

const RecordCreationFields = ({ value, onChange, disabled, level = 'l0', lockModule = false, ticketId, grouped = false, compact = false, onReady, onOptions }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    ticketService.getRecordCreationOptions(level, ticketId).then(res => {
      if (!active) return;
      if (res.success) setOptions(res.data);
      else setError(res.message || `Unable to load ${level.toUpperCase()} categories.`);
      setLoading(false);
    });
    return () => { active = false; };
  }, [attempt, level, ticketId]);

  const compactGrid = compact && level === 'l1';
  const selected = options.find(option => option.value === value.module);
  const selectedModule = selected?.value;
  const isGateway = ['payment_gateway_providers', 'payment_gateway_master_providers'].includes(selectedModule);
  useEffect(() => { onOptions?.(selected?.fields || []); }, [selected, onOptions]);
  useEffect(() => {
    onReady?.(!loading && !error && !!selectedModule);
  }, [loading, error, selectedModule, onReady]);

  const updateField = (key, fieldValue) => {
    const data = { ...value.data, [key]: fieldValue };
    if (selectedModule === 'payment_gateway_master_providers' && ['category', 'country', 'currency'].includes(key)) data.costing_rate_rules = buildGatewayRateRules(data, data.costing_rate_rules, data.costing_rates ?? 0);
    onChange({ ...value, data });
  };
  const selectModule = module => {
    const config = options.find(option => option.value === module);
    onChange({ module, data: Object.fromEntries((config?.fields || []).map(field => [field.key, field.default ?? ''])) });
  };
  useEffect(() => {
    if (level === 'l1' && options.length && !value.module) {
      const config = options.find(option => option.value === 'master_providers') || options[0];
      onChange({ module: config.value, data: Object.fromEntries(config.fields.map(field => [field.key, field.default ?? ''])) });
    }
  }, [level, options, value.module, onChange]);

  const renderField = definition => {
    const field = compact && level === 'l1' ? { ...definition, required: false } : definition;
    return (
      <div key={field.key} className={compactGrid ? `ticket-record-field${['links', 'pairs', 'multi_select', 'rate_rules'].includes(field.type) ? ' is-wide' : ['provider_id', 'source'].includes(field.key) ? ' is-reference' : ''}` : undefined}
        style={!compactGrid && ['links', 'pairs', 'multi_select', 'rate_rules'].includes(field.type) ? { gridColumn: '1 / -1' } : undefined}>
        {field.type === 'rate_rules' ? <PaymentGatewayRateTable rateLabel="Costing" provider={getTicketProviderDefinition(value.data, true, selected.fields)}
          rules={value.data.costing_rate_rules} defaultRate={value.data.costing_rates ?? 0} disabled={disabled} onChange={rules => updateField(field.key, rules)} /> : field.type === 'boolean' ? (
          <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input type="checkbox" checked={!!value.data[field.key]} onChange={event => updateField(field.key, event.target.checked)} disabled={disabled} />{field.label}
          </label>
        ) : field.type === 'lines' ? (
          <label>{field.label}<textarea className="input" value={(value.data[field.key] || []).join('\n')} disabled={disabled} onChange={event => updateField(field.key, event.target.value.split('\n'))} /></label>
        ) : ['links', 'pairs'].includes(field.type) ? (
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            <span>{field.label}</span>
            {(value.data[field.key] || []).map((item, index) => (
              <div key={index} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {(field.columns || ['name', 'url']).map((column, columnIndex) => (
                  <input key={column} className="input" type={column === 'url' ? 'url' : 'text'}
                    aria-label={`${field.label} ${index + 1} ${field.columnLabels?.[columnIndex] || column}`}
                    placeholder={field.columnLabels?.[columnIndex] || (column === 'url' ? 'https://...' : 'Name')}
                    value={item[column] || ''} disabled={disabled} style={{ flex: '1 1 160px' }}
                    onChange={event => updateField(field.key, value.data[field.key].map((row, rowIndex) => rowIndex === index ? { ...row, [column]: event.target.value } : row))} />
                ))}
                <button type="button" className="btn btn-secondary" onClick={() => updateField(field.key, value.data[field.key].filter((_, rowIndex) => rowIndex !== index))} disabled={disabled}>Remove</button>
              </div>
            ))}
            <button type="button" className="btn btn-secondary" onClick={() => updateField(field.key, [...(value.data[field.key] || []), Object.fromEntries((field.columns || ['name', 'url']).map(column => [column, '']))])} disabled={disabled}>Add {field.label}</button>
          </div>
        ) : field.type === 'multi_select' ? (
          <MultiReferenceSelect label={field.label} itemName={field.itemName} required={field.required} compact={isGateway} closeOnAdd={isGateway} value={value.data[field.key]} options={field.options}
            onChange={fieldValue => updateField(field.key, fieldValue)} disabled={disabled} />
        ) : level === 'l1' && ['provider_id', 'source'].includes(field.key) ? (
          <RecordReferenceSelect field={field} value={value.data[field.key] || ''} onChange={fieldValue => updateField(field.key, fieldValue)} disabled={disabled} />
        ) : (
          <label>
            {field.label}{field.required ? ' *' : ''}
            {field.type === 'select' ? (
              <select className="input" value={value.data[field.key] || ''} onChange={event => updateField(field.key, event.target.value)} disabled={disabled}>
                <option value="">{definition.required ? `Select ${field.label.toLowerCase()}` : 'None'}</option>
                {(field.choices || field.options || []).map(option => typeof option === 'string'
                  ? <option key={option} value={option}>{option}</option>
                  : <option key={option._id} value={option._id}>{option.code} — {option.name}</option>)}
              </select>
            ) : (
              <input className="input" type={field.type === 'number' ? 'number' : 'text'} min={field.min} max={field.max} step={field.type === 'number' ? 'any' : undefined}
                value={value.data[field.key] ?? ''} onChange={event => updateField(field.key, event.target.value)} disabled={disabled} />
            )}
          </label>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'grid', gap: '0.85rem' }}>
      {!compact && !ticketId && level !== 'update' && <p className="ticket-muted">{level === 'l1'
        ? 'Create the ticket and collect information with the assignee. Missing fields can be saved and completed later; final approval creates the L1 record.'
        : 'Request a new L0 record. Finance or Superadmin approval creates the record and completes the ticket. No merchant is required.'}</p>}
      {loading && <div role="status">Loading {level.toUpperCase()} provider options...</div>}
      {error && <div role="alert" style={{ color: 'var(--error)' }}>{error} <button type="button" className="btn btn-secondary" onClick={() => setAttempt(previous => previous + 1)} disabled={disabled}>Retry</button></div>}
      {(level === 'l0' || options.length > 1) && !lockModule && <label>
        {compact ? 'Category' : `${level.toUpperCase()} Category`} *
        <select className="input" value={value.module} onChange={event => selectModule(event.target.value)} disabled={disabled || loading || !!error}>
          <option value="">{loading ? `Loading ${level.toUpperCase()} categories...` : `Select ${level.toUpperCase()} category`}</option>
          {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </label>}
      {!compact && level === 'l1' && <p className="ticket-muted">L1 code is generated when the provider identity is complete. Required fields must be filled before final review or approval.</p>}
      {grouped ? recordCreationFieldGroups.map(group => {
        const fields = (selected?.fields || []).filter(field => (compact && ['backend_code', 'is_active'].includes(field.key) ? 'integration' : getRecordCreationFieldGroup(field.key)) === group.key);
        if (!fields.length) return null;
        return <details className="ticket-details-group" key={group.key} open={compact ? group.key === 'identity' : group.open}>
          <summary>{group.label}{!compact && <span>{group.hint}</span>}</summary>
          <div className={compactGrid ? 'ticket-record-grid' : undefined} style={compactGrid ? undefined : fieldGridStyle}>{fields.map(renderField)}</div>
        </details>;
      }) : <div style={fieldGridStyle}>{selected?.fields.map(renderField)}</div>}
    </div>
  );
};

export default RecordCreationFields;
